import {useEffect, useState} from 'react'
import db from '../../../../../services/firebase'
import {collection, doc, onSnapshot, updateDoc, getDoc, getDocs} from 'firebase/firestore'
import {useAuth} from '../../../../modules/auth'
import Drawer from 'react-modern-drawer'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import Input from 'app/components/Input'
import {toAbsoluteUrl} from '../../../../../_metronic/helpers'
import Button from 'app/components/Button'
import {AddAuditor} from '../../../settings/components/drawer/AddAuditor'
import ConfirmationModal from 'app/components/ConfirmationModal'
import Overlay from 'app/components/Overlay'

const EditAuditors = ({isOpen, onClose, auditID}) => {
  const [isLoading, setIsLoading] = useState(false)
  const {currentUser} = useAuth()
  const [frameworks, setFrameworks] = useState<string[]>([])
  const [assignedAuditors, setAssignedAuditors] = useState<any[]>([])
  const [auditTitle, setAuditTitle] = useState('')
  const [audit, setAudit] = useState<any>(null)
  const [isAddAuditorOpen, setIsAddAuditorOpen] = useState(false)
  const [availableAuditors, setAvailableAuditors] = useState<any[]>([])
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false)
  const [showAddConfirmation, setShowAddConfirmation] = useState(false)
  const [selectedAuditor, setSelectedAuditor] = useState<any>(null)

  useEffect(() => {
    getAudits()
  }, [auditID])

  useEffect(() => {
    if (audit?.auditorOrganization) {
      fetchAvailableAuditors()
    }
  }, [audit?.auditorOrganization, audit])

  const getAudits = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId || !auditID) {
        return
      }
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'audits')
      const riskDocRef = doc(PolicyRef, auditID)

      const unsubscribe = onSnapshot(riskDocRef, (doc) => {
        if (doc.exists()) {
          const audit = doc.data()
          setFrameworks(audit.framework)

          const auditors =
            audit.auditor && Array.isArray(audit.auditor)
              ? audit.auditor
              : audit.auditor
              ? [audit.auditor]
              : []

          setAssignedAuditors(auditors)
          setAuditTitle(audit.auditTitle || '')
          setAudit(audit)
        }
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting risk:', error)
    }
  }

  const handleRemoveSpecificAuditor = (auditor: any) => {
    if (assignedAuditors.length === 1) {
      showErrorToast({message: 'Cannot remove the last auditor. At least one auditor must be assigned to the audit.'})
      return
    }
    
    setSelectedAuditor(auditor)
    setShowRemoveConfirmation(true)
  }

  const confirmRemoveAuditor = async () => {
    if (!selectedAuditor) return

    if (selectedAuditor.id === currentUser?.uid || selectedAuditor.email === currentUser?.email) {
      showErrorToast({message: 'You cannot remove yourself from the audit'})
      setShowRemoveConfirmation(false)
      setSelectedAuditor(null)
      return
    }

    setIsLoading(true)
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const auditRef = doc(tenantRef, 'audits', auditID)

      const auditDoc = await getDoc(auditRef)
      const currentAuditData = auditDoc.data()

      const existingAuditors = currentAuditData?.auditor || []
      const auditorsArray = Array.isArray(existingAuditors) ? existingAuditors : []

      const updatedAuditors = auditorsArray.filter(
        (auditor: any) => auditor.id !== selectedAuditor.id
      )

      await updateDoc(auditRef, {
        auditor: updatedAuditors.length > 0 ? updatedAuditors : null,
        updatedAt: new Date().toISOString().split('T')[0],
      })

      const employeeDocRef = doc(tenantRef, 'employees', selectedAuditor.id)
      await updateDoc(employeeDocRef, {availability: true})

      showSuccessToast({message: 'Auditor removed successfully'})

      await fetchAvailableAuditors()
    } catch (error) {
      console.error('Error removing auditor:', error)
      showErrorToast({message: 'Failed to remove auditor'})
    } finally {
      setIsLoading(false)
      setShowRemoveConfirmation(false)
      setSelectedAuditor(null)
    }
  }

  const handleOpenAddAuditor = () => {
    setIsAddAuditorOpen(true)
  }

  const fetchAvailableAuditors = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId || !audit?.auditorOrganization) {
        return
      }

      const tenantRef = doc(db, 'tenants', tenantId)
      const employeesRef = collection(tenantRef, 'employees')

      const querySnapshot = await getDocs(employeesRef)
      const auditors = querySnapshot.docs
        .map((doc) => ({id: doc.id, availability: true, ...doc.data()}))
        .filter(
          (employee: any) =>
            employee.role === 'Auditor' && employee.organization === audit.auditorOrganization
        )
        .filter((auditor) => auditor.availability === true)

      setAvailableAuditors(auditors)
    } catch (error) {
      console.error('Error fetching auditors:', error)
    }
  }

  const handleAssignAuditor = (auditor: any) => {
    setSelectedAuditor(auditor)
    setShowAddConfirmation(true)
  }

  const confirmAssignAuditor = async () => {
    if (!selectedAuditor) return

    setIsLoading(true)
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const auditRef = doc(tenantRef, 'audits', auditID)

      const auditDoc = await getDoc(auditRef)
      const currentAuditData = auditDoc.data()

      const existingAuditors = currentAuditData?.auditor || []
      const auditorsArray = Array.isArray(existingAuditors) ? existingAuditors : []

      const isAlreadyAssigned = auditorsArray.some(
        (existingAuditor: any) => existingAuditor.id === selectedAuditor.id
      )

      if (isAlreadyAssigned) {
        showErrorToast({message: 'Auditor is already assigned to this audit'})
        return
      }

      const newAuditor = {
        fullName: selectedAuditor.full_name,
        email: selectedAuditor.email,
        id: selectedAuditor.id,
      }

      const updatedAuditors = [...auditorsArray, newAuditor]

      await updateDoc(auditRef, {
        auditor: updatedAuditors,
        updatedAt: new Date().toISOString().split('T')[0],
      })

      const employeeDocRef = doc(tenantRef, 'employees', selectedAuditor.id)
      await updateDoc(employeeDocRef, {availability: false})

      setAssignedAuditors(updatedAuditors)
      showSuccessToast({message: 'Auditor assigned successfully'})

      await fetchAvailableAuditors()
    } catch (error) {
      console.error('Error assigning auditor:', error)
      showErrorToast({message: 'Failed to assign auditor'})
    } finally {
      setIsLoading(false)
      setShowAddConfirmation(false)
      setSelectedAuditor(null)
    }
  }

  return (
    <>
      <Drawer open={isOpen} onClose={onClose} direction='right' className='' size='45%'>
        <div
          className='modal-dialog-centered border-bottom border-gray-300'
          style={{
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            padding: '14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            paddingLeft: '15px',
            paddingRight: '12px',
          }}
        >
          <div className='fs-4 fw-bold mx-2' style={{margin: 0}}>
            Edit Auditors
          </div>
          <div style={{display: 'flex', gap: '16px'}}>
            <div
              className='btn btn-icon btn-sm btn-active-light-primary'
              onClick={onClose}
              aria-label='Close'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='20'
                height='20'
                viewBox='0 0 24 24'
                fill='none'
                stroke='#5A5A5A'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-x'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M18 6l-12 12' />
                <path d='M6 6l12 12' />
              </svg>
            </div>
          </div>
        </div>
        <div
          className='p-6 scroll-y overflow-auto'
          tabIndex={-1}
          style={{height: 'calc(100vh - 50px)'}}
        >
          <div className='row mb-5'>
            <div className='col-3'>
              <label className='text-gray-800 fw-semibold me-2 fs-6 '>Company</label>
            </div>
            <div className='col-9'>
              <span className='fw-normal fs-6'>{audit?.auditorOrganization || ''}</span>
            </div>
          </div>
          <div className='row mb-5'>
            <div className='col-3'>
              <label className='text-gray-800 fw-semibold me-2 fs-6 '>Audit Title</label>
            </div>
            <div className='col-9'>
              <span className='fw-normal fs-6'>{auditTitle}</span>
            </div>
          </div>
          <div className='row mb-5'>
            <div className='col-3'>
              <label className='text-gray-800 fw-semibold me-2 fs-6 '>Audit Type</label>
            </div>
            <div className='col-9'>
              <span className='fw-normal fs-6'>{frameworks}</span>
            </div>
          </div>
          {assignedAuditors.length > 0 && (
            <>
              <div className='d-flex flex-column mb-5 mt-6'>
                <div className='pb-1 mb-5 border-bottom border-gray-300'>
                  <span className='fw-medium text-dark fs-6'>Auditors Assigned</span>
                </div>
                {assignedAuditors.map((auditor, index) => (
                  <div key={auditor.id || index} className='flex-fill d-flex gap-3 mb-3'>
                    <Input
                      type='text'
                      value={auditor.fullName || ''}
                      readOnly
                      onFocus={(e) => {
                        e.target.style.outline = 'none'
                      }}
                    />
                    <Input
                      type='text'
                      value={auditor.email || ''}
                      readOnly
                      onFocus={(e) => {
                        e.target.style.outline = 'none'
                      }}
                    />
                    <Button
                      style={{
                        width: '43.5px',
                        height: '43.5px',
                        opacity:
                          auditor.id === currentUser?.uid || 
                          auditor.email === currentUser?.email ||
                          assignedAuditors.length === 1
                            ? '0.5'
                            : '1',
                      }}
                      onClick={() => {
                        handleRemoveSpecificAuditor(auditor)
                      }}
                      disabled={
                        auditor.id === currentUser?.uid || 
                        auditor.email === currentUser?.email ||
                        assignedAuditors.length === 1
                      }
                      title={
                        auditor.id === currentUser?.uid || auditor.email === currentUser?.email
                          ? 'Cannot remove yourself'
                          : assignedAuditors.length === 1
                          ? 'Cannot remove the last auditor'
                          : 'Remove auditor'
                      }
                    >
                      <img
                        alt='trash-icon'
                        src={toAbsoluteUrl('/media/icon/trash.svg')}
                        className=''
                        style={{height: '18px', width: '18px'}}
                      />
                    </Button>
                  </div>
                ))}
              </div>
              <div className='d-flex flex-column'>
                <div className='d-flex justify-content-between align-items-center pb-1 mb-5 border-bottom border-gray-300'>
                  <span className='fw-medium text-dark fs-6'>
                    Add Auditors
                    <Overlay
                      content='Once the auditor is invited, they will receive an email containing the audit link, and will only appear in this section after completing 
                      their signup process, at which point you can assign the audit to them.'
                      placement='right'
                    />
                  </span>

                  <Button btnType='transparent' onClick={handleOpenAddAuditor}>
                    <img
                      alt='plus-icon'
                      src={toAbsoluteUrl('/media/icon/plus.svg')}
                      className='me-2'
                      style={{height: '18px', width: '18px'}}
                    />
                    <span>Invite Auditor</span>
                  </Button>
                </div>
                <div className='d-flex flex-column mb-5'>
                  {availableAuditors.length > 0 ? (
                    availableAuditors.map((auditor) => (
                      <div key={auditor.id} className='flex-fill d-flex gap-3 mb-3'>
                        <Input
                          type='text'
                          value={auditor.full_name || ''}
                          readOnly
                          onFocus={(e) => {
                            e.target.style.outline = 'none'
                          }}
                        />
                        <Input
                          type='text'
                          value={auditor.email || ''}
                          readOnly
                          onFocus={(e) => {
                            e.target.style.outline = 'none'
                          }}
                        />
                        <Button
                          style={{
                            width: '43.5px',
                            height: '43.5px',
                          }}
                          onClick={() => {
                            handleAssignAuditor(auditor)
                          }}
                        >
                          <img
                            alt='plus-icon'
                            src={toAbsoluteUrl('/media/icon/plus.svg')}
                            className=''
                            style={{height: '18px', width: '18px'}}
                          />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className='text-center py-8 mt-10'>
                      <div className='mb-4'>
                        <img
                          src='/media/buttons/EmptyState-File.svg'
                          alt='No auditors'
                          style={{width: '40px', height: '40px'}}
                        />
                      </div>
                      <div className='text-gray-900 fs-6 fw-semibold mb-2'>
                        No Auditors Available
                      </div>
                      <div className='text-gray-900 fs-7'>
                        No auditors found for this organization. Invite an auditor to get started.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </Drawer>

      <AddAuditor
        isOpen={isAddAuditorOpen}
        onClose={() => setIsAddAuditorOpen(false)}
        organization={audit?.auditorOrganization || ''}
      />

      {/* Remove Auditor Confirmation Modal */}
      <ConfirmationModal
        isModalOpen={showRemoveConfirmation}
        hideModal={() => {
          setShowRemoveConfirmation(false)
          setSelectedAuditor(null)
        }}
        confirmBtnClickHandler={confirmRemoveAuditor}
        toDeleteItemTitle={selectedAuditor?.fullName || 'auditor'}
        isLoading={isLoading}
        title='Remove Auditor'
        message={`Are you sure you want to remove ${
          selectedAuditor?.fullName || 'this auditor'
        } from this audit? This action cannot be undone.`}
        confirmButtonText='Remove'
        type='delete'
      />

      {/* Add Auditor Confirmation Modal */}
      <ConfirmationModal
        isModalOpen={showAddConfirmation}
        hideModal={() => {
          setShowAddConfirmation(false)
          setSelectedAuditor(null)
        }}
        confirmBtnClickHandler={confirmAssignAuditor}
        toDeleteItemTitle={selectedAuditor?.full_name || 'auditor'}
        isLoading={isLoading}
        title='Assign Auditor'
        message={`Are you sure you want to assign ${
          selectedAuditor?.full_name || 'this auditor'
        } to this audit?`}
        confirmButtonText='Assign'
        type='activate'
      />
    </>
  )
}

export default EditAuditors
